import {
  AssignmentPeriod,
  CompensationPayload,
  EvaluateHeapedPayload,
  EvaluateLevelPayload,
  RetrieveCompensationPayload,
} from 'Quoting/models/DTOs/CompensationPayload';
import {InitDto} from 'Quoting/models/DTOs/Init';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {getProductCdFromModel} from 'Quoting/utils/helpers';

type AttributeValue =
  | {valueType: 'String'; stringValue: string}
  | {valueType: 'Integer'; integerValue: number}
  | {valueType: 'Datetime'; datetimeValue: string};

type CommonAttributeType = {
  id: string;
  entityName: string;
  attributeName: string;
};

interface Attribute extends CommonAttributeType {
  _type: string;
  attributeValue: {_type: 'Value'} & AttributeValue;
}

interface Dimension extends CommonAttributeType {
  attributeValue: AttributeValue;
}

interface RetrievePlansWithCoverageRequestPayload {
  attributes: Attribute[];
  dimensions: Dimension[];
  rateEffectiveDate: string;
  rateRequestDate: string;
  _type: string;
}

export interface RetrievePlansRequestData {
  productModel: A360ProductModel;
  sicCode: string;
  numberOfEligibleLives: number;
  riskStateCode: string;
  termEffectiveDate: string;
  rateEffectiveDate: string;
  rateRequestDate: string;
}

export const getRetrievePlansWithCoverageRequestPayload = (requestData: RetrievePlansRequestData) => {
  const {
    productModel,
    sicCode,
    numberOfEligibleLives,
    riskStateCode,
    termEffectiveDate,
    rateEffectiveDate,
    rateRequestDate,
  } = requestData;

  const productCd = getProductCdFromModel(productModel);

  const requestPayload: RetrievePlansWithCoverageRequestPayload = {
    attributes: [
      {
        _type: 'DataItem',
        id: '/',
        entityName: `${productCd}MasterPolicySummary`,
        attributeName: 'sicCode',
        attributeValue: {
          _type: 'Value',
          valueType: 'String',
          stringValue: sicCode,
        },
      },
    ],
    dimensions: [
      {
        attributeName: 'numberOfEligibleLives',
        entityName: `${productCd}EligibleLivesAndParticipation`,
        id: '/',
        attributeValue: {
          valueType: 'Integer',
          integerValue: numberOfEligibleLives,
        },
      },
      {
        attributeValue: {
          stringValue: riskStateCode,
          valueType: 'String',
        },
        entityName: `${productCd}MasterPolicySummary`,
        attributeName: 'riskStateCd',
        id: '/',
      },
    ],
    rateEffectiveDate: rateEffectiveDate,
    rateRequestDate: rateRequestDate,
    _type: 'BehaviorRequest',
  };

  if (productCd === A360ProductCd.HI) {
    requestPayload.attributes = requestPayload.attributes.concat({
      _type: 'DataItem',
      id: '/termDetails',
      entityName: 'HITermDetails',
      attributeName: 'termEffectiveDate',
      attributeValue: {
        _type: 'Value',
        valueType: 'Datetime',
        datetimeValue: termEffectiveDate,
      },
    });
  }
  return requestPayload;
};

export const deleteState = (payload: CompensationPayload) => {
  delete payload.state;
  delete payload.contract.state;

  return payload;
};

export const getCompensationPayload = (compPayload: RetrieveCompensationPayload) => {
  const {
    policyNumberValue,
    start,
    initResponse,
    premiumSplitPercentage,
    productCode,
    rate,
    code,
    premiumSplitId,
    compType,
  } = compPayload;
  // @ts-ignore
  let compensationDetails: CompensationPayload = initResponse;
  const assignmentPeriod: AssignmentPeriod = {
    _type: 'Period',
    start: start,
  };
  compensationDetails.contract.processingDefinition = {
    calculationDefinition: {
      // @ts-ignore
      rule: compType === 'Level' ? getLevelRule(rate) : getHeapedRule(rate),
      compensationType: 'Commission',
      refundable: true,
      _type: 'CalculationDefinitionEntity',
      includeInRating: true,
    },
    _type: 'ContractProcessingDefinitionEntity',
  };

  compensationDetails.contract.applicabilityParameters = {
    _type: 'ContractApplicabilityParameters',
    applicabilityPeriod: assignmentPeriod,
  };

  compensationDetails.assignmentPeriod = assignmentPeriod;

  compensationDetails.compensationSubject = {
    productCode: productCode,
    policyNumber: policyNumberValue,
    premiumSplitPercentage: premiumSplitPercentage,
    compensableItem: {
      _type: 'CompensableItem',
      codes: [],
      type: 'COVERAGE',
    },
    _type: 'CompensationSubject',
  };

  compensationDetails.owner = {
    agency: {
      code: code,
      type: 'AGENCY',
      _type: 'Agency',
    },
    premiumSplitId: premiumSplitId,
    slotId: '1',
    splitPercentage: null,
    _type: 'CompensationHolderEntity',
  };

  return compensationDetails;
};

export const getLevelRule = (rate: string) => {
  return {
    rate: {
      rate: parseFloat(rate),
      denomination: 'PERCENTAGE',
      _type: 'CompensationRate',
    },
    _type: 'FlatContractRuleEntity',
    type: 'FlatPercent',
  };
};
export const getHeapedRule = (rate: string) => {
  const rates = rate.split(' / ');
  return {
    bounds: [
      {
        denomination: 'PERCENTAGE',
        _type: 'HeapedCompensationRate',
        lower: 1,
        upper: 1,
        rate: parseFloat(rates[0]),
      },
      {
        denomination: 'PERCENTAGE',
        _type: 'HeapedCompensationRate',
        lower: 2,
        upper: 100,
        rate: parseFloat(rates[1]),
      },
    ],
    type: 'IndividualHeapedPercent',
    _type: 'HeapedContractRuleEntity',
  };
};

export interface DeletePayload {
  contractAssignmentKey: string;
  contractKey: string;
}

export const getDeletePayload = (initData: InitDto | CompensationPayload) => {
  const payload: DeletePayload = {
    contractAssignmentKey: initData._key.rootId,
    contractKey: initData.contract._key.rootId,
  };

  return payload;
};

export const getEvaluatePayload = (values: any, type: string) => {
  const {packageCd, riskStateCd, planCd, rateRequestDate, termEffectiveDate, techFeePct, coverageBasisCd} = values;

  const dimensions = {
    packageCd: packageCd,
    riskStateCd: riskStateCd,
    planCd: planCd,
    rateRequestDate: rateRequestDate,
    termEffectiveDate: termEffectiveDate,
  };

  const level: EvaluateLevelPayload = {
    dimensions: dimensions,
    parameters: {
      techFeePct: techFeePct,
      coverageBasisCd: coverageBasisCd,
    },
  };

  const heaped: EvaluateHeapedPayload = {
    dimensions: dimensions,
  };

  return type === 'Level' ? level : heaped;
};
